/**
 * Les échéances d'une micro-entreprise, calculées depuis sa date de début.
 *
 * TOUT SITE PUBLIE LES MÊMES DATES GÉNÉRIQUES, QUE PERSONNE NE RETIENT.
 * « L'ACRE se demande dans les soixante jours » ne dit rien à celui qui s'est
 * déclaré le 12 mars : il lui faut le 11 mai. Ce module transforme une date de
 * déclaration en dates à lui, que la rubrique affiche en chronologie et que le
 * lecteur emporte dans son agenda.
 *
 * AUCUNE DATE N'EST INVENTÉE ICI. Chaque échéance porte la clé du référentiel
 * qui la fonde (docs/glossaries/micro-entreprise/reference-2026.json), et rien
 * n'entre dans cette liste sans elle. Les dates que le référentiel ne tranche
 * pas restent dehors : la première déclaration Urssaf, dont le délai dépend du
 * rythme choisi au guichet, et la déclaration annuelle de revenus, dont la
 * date change chaque printemps. Les mettre au jugé dans l'agenda de quelqu'un
 * serait pire que de ne rien mettre.
 *
 * DEUX ÉCHÉANCES DE CFE, ET ELLES NE SE RESSEMBLENT PAS. La déclaration
 * initiale 1447-C se dépose au 31 décembre de l'année de création, et la
 * première cotisation ne tombe qu'un an plus tard : l'année de création est
 * exonérée. Beaucoup de simulateurs annoncent une CFE dès la première année,
 * et affolent pour rien.
 */

/** Une date sans heure, pour que rien ne dérive d'un fuseau à l'autre. */
function jour(annee, mois, date) {
    return new Date(annee, mois, date, 12, 0, 0, 0);
}

function ajouterJours(depart, nombre) {
    const d = new Date(depart);
    d.setDate(d.getDate() + nombre);
    d.setHours(12, 0, 0, 0);
    return d;
}

/** Le dernier jour du mois, quel que soit sa longueur. */
function finDeMois(annee, mois) {
    return jour(annee, mois + 1, 0);
}

/** Le trimestre civil d'une date, de 0 à 3. */
function trimestre(date) {
    return Math.floor(date.getMonth() / 3);
}

/**
 * Les échéances d'une première année, dans l'ordre où elles tombent.
 *
 * @param dateDebut    le début d'activité déclaré au guichet unique.
 * @param aujourdhui   la date de lecture, pour savoir ce qui est déjà passé.
 * @returns la liste, chaque entrée portant sa source et l'outil qui la traite.
 */
export function echeancesPremiereAnnee({ dateDebut, aujourdhui = new Date() }) {
    if (!(dateDebut instanceof Date) || Number.isNaN(dateDebut.getTime())) {
        return [];
    }
    const annee = dateDebut.getFullYear();

    // L'Acre court jusqu'à la fin du 3e trimestre civil qui suit celui du
    // début d'activité : de neuf à douze mois selon la date, jamais douze
    // mois pleins comme au réel.
    const trimestreDebut = trimestre(dateDebut);
    const trimestresEcoules = trimestreDebut + 3;
    const anneeFinAcre = annee + Math.floor(trimestresEcoules / 4);
    const moisFinAcre = ((trimestresEcoules % 4) + 1) * 3 - 1;

    const liste = [
        {
            cle: "acre-demande",
            date: ajouterJours(dateDebut, 60),
            outil: "demande-acre",
            source: "acre_delai_demande",
        },
        {
            cle: "vfl-option",
            // Pour une création, l'option se dépose jusqu'au dernier jour du
            // 3e mois qui suit celui de la création.
            date: finDeMois(annee, dateDebut.getMonth() + 3),
            outil: "versement-liberatoire",
            source: "vfl_dates_option",
        },
        {
            cle: "acre-fin",
            date: finDeMois(anneeFinAcre, moisFinAcre),
            outil: "acre",
            source: "acre_duree_micro",
        },
        {
            cle: "cfe-declaration",
            date: jour(annee, 11, 31),
            outil: "cfe-declaration-initiale",
            source: "cfe_declarations",
        },
        {
            cle: "cfe-premier-solde",
            // L'année de création ne paie pas de CFE : le premier solde tombe
            // au 15 décembre de l'année suivante.
            date: jour(annee + 1, 11, 15),
            outil: "reclamation-cfe",
            source: "cfe_paiement",
        },
    ];

    return liste
        .map((e) => ({
            ...e,
            passee: e.date < aujourdhui,
            joursRestants: Math.ceil(
                (e.date - aujourdhui) / (1000 * 60 * 60 * 24),
            ),
        }))
        .sort((a, b) => a.date - b.date);
}

/** La prochaine échéance à venir, celle qui compte quand on arrive sur la page. */
export function prochaineEcheance(echeances) {
    return echeances.find((e) => !e.passee) || null;
}
